import { AlertTriangle, Ban, Check, Loader2, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

import type { Verdict } from '../../verdictPolicy';
import {
  MODAL_GUTTER,
  ModalCheckbox,
  ModalPrimaryButton,
  ModalRadio,
  ModalSecondaryButton,
  ModalSubtitle,
  SessionModalShell,
} from './SessionModalShell';

/**
 * "Approve session" / "Decline session" / "Request exception" — the designer's
 * `ApproveSessionModal` and `RejectSessionModal`, which are the same dialog in two
 * colours: a grid of reason radios, an optional note, and a "Send email to user" block.
 *
 * One component rather than two files, because the designer's own CSS says so — the two
 * stylesheets are identical apart from the accent colour (#00C232 vs #BF3636) and the
 * reason list, and the footer button differs only in tone and label. Keeping them as one
 * parameterised dialog is what stops the third variant (Exception) from becoming a third
 * near-copy.
 *
 * Faithful to the designer: 500px container, the 2-column reason grid on `sm`, 1.5px
 * #ECEEF3 borders going accent-coloured with a 0D tint when selected, the reason label
 * turning semibold, the #ECEEF3 divider, and the 18px square checkbox.
 *
 * WHAT WE SEND: the chosen reason IS the audit note — combined with the free-text comment
 * as "Reason — comment" when one is typed. That is also what satisfies the API's
 * note-required rule, so a reason radio can never be left effectively blank.
 */

export type DecisionVerdict = Extract<Verdict, 'Approved' | 'Rejected' | 'ExceptionRequested'>;

interface DecisionConfig {
  title: string;
  /** The designer's accent for this verdict — the same values as the status badges. */
  color: string;
  Icon: typeof Check;
  buttonLabel: string;
  ButtonIcon: typeof Check;
  tone: 'primary' | 'danger';
  reasons: readonly string[];
  /** The designer preselects a reason rather than opening with none chosen. */
  defaultReason: string;
  notePlaceholder: string;
}

const CONFIG: Record<DecisionVerdict, DecisionConfig> = {
  Approved: {
    title: 'Approve session',
    color: '#00C232',
    Icon: Check,
    buttonLabel: 'Approve session',
    ButtonIcon: Check,
    tone: 'primary',
    // The designer's four, verbatim.
    reasons: ['Passed verification', 'Manual override', 'False positive', 'Other'],
    defaultReason: 'Manual override',
    notePlaceholder: 'Add a comment (optional – it will stay visible on Session details)',
  },
  Rejected: {
    // "Decline", not "Reject" — the designer's wording for the admin's own action, which
    // reads less like the system's automatic Rejected.
    title: 'Decline session',
    color: '#BF3636',
    Icon: Ban,
    buttonLabel: 'Decline session',
    ButtonIcon: Ban,
    tone: 'danger',
    // The designer's six, verbatim.
    reasons: [
      'Known fraud',
      'Suspected document tampering',
      'Suspicious behaviour',
      'Document mismatch',
      'Liveness does not match document photo',
      'Other',
    ],
    defaultReason: 'Document mismatch',
    notePlaceholder: 'Add note (visible on Session details)',
  },
  ExceptionRequested: {
    // OURS, not the designer's — there is no exception dialog in the reference. Same
    // shape and the same colour as its status badge, so it belongs to the same family.
    title: 'Request exception',
    color: '#FF383C',
    Icon: AlertTriangle,
    buttonLabel: 'Send exception request',
    ButtonIcon: AlertTriangle,
    tone: 'primary',
    reasons: [
      'Incomplete run — accept anyway',
      'Expired before completion',
      'Locked out after too many attempts',
      'Policy exception',
      'Other',
    ],
    defaultReason: 'Policy exception',
    notePlaceholder: 'Explain what the reviewer is being asked to approve',
  },
};

export interface DecisionTarget {
  referenceId?: string;
  applicantName: string;
  originalStatus: string;
  originalStatusLabel: string;
  originalStatusColor: string;
  /** Prefills the email field when the run has an address on it. */
  email?: string | null;
}

export function SessionDecisionModal({
  open,
  verdict,
  target,
  isSaving,
  errorMessage,
  onClose,
  onSubmit,
}: {
  open: boolean;
  verdict: DecisionVerdict | null;
  target: DecisionTarget | null;
  isSaving: boolean;
  errorMessage?: string | null;
  onClose: () => void;
  onSubmit: (note: string) => void;
}) {
  const groupId = useId();
  const config = verdict ? CONFIG[verdict] : null;
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [email, setEmail] = useState('');

  // Reset when the dialog opens on a verdict, not on every render. Keyed so reopening on
  // the same verdict still starts from the designer's default rather than from whatever
  // was typed and abandoned last time.
  const initialisedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !config) {
      initialisedFor.current = null;
      return;
    }
    const key = `${verdict}`;
    if (initialisedFor.current === key) return;
    initialisedFor.current = key;
    setReason(config.defaultReason);
    setNote('');
    setSendEmail(false);
    setEmail(target?.email ?? '');
  }, [open, config, verdict, target?.email]);

  if (!open || !verdict || !config || !target) return null;

  // The reason always carries the audit; the comment refines it. Sent as one string
  // because that is what the API stores — one note per decision.
  const composedNote = note.trim() ? `${reason} — ${note.trim()}` : reason;

  return (
    <SessionModalShell
      open={open}
      onClose={onClose}
      // 620px, not the designer's 500px. Their longest reason label ("Liveness does not
      // match document photo") needs ~250px of text on its own line, and two of those plus
      // radios, padding and the gutters do not fit in 500 — which is why the right-hand
      // column was being cut off. Widening keeps their type size and their 2-column grid.
      maxWidth="620px"
      icon={<config.Icon size={16} style={{ color: config.color }} />}
      title={config.title}
      subtitle={
        <ModalSubtitle
          referenceId={target.referenceId}
          applicantName={target.applicantName}
          status={target.originalStatusLabel}
          statusColor={target.originalStatusColor}
        />
      }
      footer={
        <>
          <ModalSecondaryButton onClick={onClose} disabled={isSaving}>
            <X size={15} />
            Cancel
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={() => onSubmit(composedNote)}
            disabled={isSaving}
            tone={config.tone}
          >
            {isSaving ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <config.ButtonIcon size={16} />
            )}
            {isSaving ? 'Saving…' : config.buttonLabel}
          </ModalPrimaryButton>
        </>
      }
    >
      <div className={cn('py-[clamp(14px,1.2vw,18px)]', MODAL_GUTTER)}>
        {/* ── Select a reason ── */}
        <div className="mb-[clamp(10px,0.9vw,14px)] flex flex-col">
          <h3 className="m-0 mb-[clamp(6px,0.6vw,8px)] text-[clamp(13px,0.95vw,14px)] font-semibold leading-tight text-[#041A3A]">
            Select a reason
          </h3>
          <div className="grid grid-cols-1 gap-[clamp(6px,0.6vw,8px)] sm:grid-cols-2">
            {config.reasons.map((value) => {
              const isSelected = reason === value;
              return (
                <label
                  key={value}
                  // `min-w-0` matters here: a grid item's default min-width is its
                  // CONTENT width, so "Liveness does not match document photo" on one
                  // unbreakable line was setting the column's minimum and forcing the whole
                  // dialog wider than its own container, which then clipped it. Releasing
                  // the minimum lets the label wrap onto a second line instead.
                  className={cn(
                    'flex min-w-0 cursor-pointer items-center gap-[clamp(8px,0.8vw,10px)] rounded-lg border-[1.5px] px-[clamp(10px,1vw,12px)] py-[clamp(7px,0.7vw,9px)] transition-colors duration-150',
                    isSelected
                      ? ''
                      : 'border-[#ECEEF3] bg-white hover:border-[#D0D4E4] hover:bg-[#FAFBFD]',
                  )}
                  style={
                    isSelected
                      ? { borderColor: config.color, backgroundColor: `${config.color}0D` }
                      : undefined
                  }
                >
                  <span className="relative flex h-[18px] w-[18px] shrink-0 items-center justify-center">
                    <input
                      type="radio"
                      name={`${groupId}-reason`}
                      value={value}
                      checked={isSelected}
                      onChange={() => setReason(value)}
                      className="absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
                    />
                    <ModalRadio checked={isSelected} color={config.color} />
                  </span>
                  <span
                    className={cn(
                      'min-w-0 flex-1 text-[clamp(12px,0.9vw,13px)] leading-snug text-[#041A3A]',
                      isSelected ? 'font-semibold' : 'font-medium',
                    )}
                  >
                    {value}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        {/* ── Add note ── */}
        <div className="mb-[clamp(10px,0.9vw,14px)] flex flex-col">
          <h3 className="m-0 mb-[clamp(6px,0.6vw,8px)] text-[clamp(13px,0.95vw,14px)] font-semibold leading-tight text-[#041A3A]">
            Add note (optional)
          </h3>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            // The reason and the separator take part of the 1000 the API stores, so the
            // comment is capped below it rather than being silently truncated server-side.
            maxLength={800}
            placeholder={config.notePlaceholder}
            className="w-full resize-none rounded-lg border border-[#ECEEF3] bg-white px-[clamp(10px,1vw,12px)] py-[clamp(8px,0.8vw,10px)] text-[clamp(12px,0.9vw,13px)] font-normal leading-relaxed text-[#041A3A] outline-none transition-colors placeholder:text-[#AAAAAA] focus:border-[#0048FF]"
          />
        </div>

        <div className="mb-[clamp(10px,0.9vw,12px)] h-px w-full bg-[#ECEEF3]" />

        {/* ── Send email to user ──
             Laid out exactly as the designer has it, but DISABLED: there is no
             decision-notification email in the API yet (it arrives with the send/resend
             work). A working-looking checkbox that quietly notifies nobody would be worse
             than one that says so. */}
        <div className="mb-[clamp(8px,0.8vw,12px)] flex min-w-0 flex-col">
          <label
            className="relative mb-[clamp(10px,0.9vw,14px)] flex min-w-0 cursor-not-allowed flex-wrap items-center gap-[clamp(8px,0.7vw,10px)] opacity-60"
            title="Decision emails are not sent yet — this arrives with the resend work."
          >
            <input
              type="checkbox"
              checked={sendEmail}
              onChange={(e) => setSendEmail(e.target.checked)}
              disabled
              className="sr-only"
            />
            <ModalCheckbox checked={sendEmail} />
            <span className="text-[clamp(13px,1vw,14.5px)] font-semibold text-[#041A3A]">
              Send email to user
            </span>
            <span className="text-[clamp(10.5px,0.8vw,11.5px)] font-normal text-[#888888]">
              (not available yet)
            </span>
          </label>

          {sendEmail && (
            <div className="flex w-full flex-col gap-1">
              <span className="text-[clamp(11px,0.85vw,12px)] font-normal text-[#888888]">
                Email
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full rounded-lg border border-[#D0D4E4] bg-white px-[clamp(12px,1vw,14px)] py-[clamp(8px,0.9vw,10px)] text-[clamp(12.5px,0.95vw,14px)] font-normal text-[#041A3A] outline-none transition-colors focus:border-[#0048FF]"
              />
            </div>
          )}
        </div>

        {errorMessage && (
          <p className="m-0 text-[clamp(11px,0.85vw,12.5px)] leading-snug text-[#BF3636]">
            {errorMessage}
          </p>
        )}
      </div>
    </SessionModalShell>
  );
}
